import { TaxBitTransaction } from "src/store/features/api/generated";

const getFiscalYearRange = (year: number) => ({
  startDate: `${year}-01-01T00:00:00Z`,
  endDate: `${year + 1}-01-01T00:00:00Z`,
});

export const buildTaxTransactionsArg = (taxYear: number) => {
  const range = getFiscalYearRange(taxYear);
  return { startDate: range.startDate, endDate: range.endDate, pageSize: 100 };
};

const usdFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const unitsFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 8,
});

const utcDateTimeFormatter = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: "UTC",
});

const utcDateFormatter = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});

export const formatUsd = (amount?: string | number) => {
  if (amount == null || amount === "") return "—";
  const value = typeof amount === "number" ? amount : Number(amount);
  if (!Number.isFinite(value)) return "—";
  return usdFormatter.format(value);
};

export const formatUnits = (amount?: string | number) => {
  if (amount == null || amount === "") return "—";
  const value = typeof amount === "number" ? amount : Number(amount);
  if (!Number.isFinite(value)) return "—";
  return unitsFormatter.format(value);
};

const parseDate = (datetime?: string) => {
  if (!datetime) return undefined;
  const value = new Date(datetime);
  return Number.isNaN(value.getTime()) ? undefined : value;
};

export const formatUtcDateTime = (datetime?: string) => {
  const value = parseDate(datetime);
  return value ? utcDateTimeFormatter.format(value) : "—";
};

export const formatUtcDate = (datetime?: string) => {
  const value = parseDate(datetime);
  return value ? utcDateFormatter.format(value) : "—";
};

export const sortReleasedDocumentsByDate = <T extends { created_date: string }>(items: T[]) =>
  [...items].sort(
    (a, b) => new Date(b.created_date).getTime() - new Date(a.created_date).getTime(),
  );

const csvEscape = (value: string) => (/[",\n]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value);

export const downloadTransactionsCsv = (transactions: TaxBitTransaction[], taxYear: number) => {
  const header = [
    "transaction_id",
    "asset",
    "quantity",
    "deposit_datetime_utc",
    "cost_basis_usd",
    "status",
  ];
  const rows = transactions.map((transaction) =>
    [
      transaction.client_transaction_id ?? transaction.id,
      transaction.asset_amount?.asset?.code ?? "",
      transaction.asset_amount?.amount ?? "",
      transaction.datetime ?? "",
      transaction.cost_basis?.amount ?? "",
      transaction.missing_cost_basis
        ? "needs_review"
        : transaction.has_transfer_lot_data
          ? "modified"
          : "completed",
    ]
      .map(csvEscape)
      .join(","),
  );
  const blob = new Blob([[header.join(","), ...rows].join("\n")], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `tax-center-deposits-${taxYear}.csv`;
  anchor.click();
  URL.revokeObjectURL(url);
};
